import isEqual from 'lodash-es/isEqual';
import { FieldPath, FieldValues, get, set } from 'react-hook-form';
import {
  ApplicantType as ApplicantTypeDetail,
  ApplicationFormValues,
  EmploymentType,
  EnterpriseStructure,
  RepaymentType,
} from '@brighte/validation';
import {
  AdditionalIncome,
  Applicant,
  Application,
  ApplicationDetailsQuery,
  ApplicationDocument,
  IdentificationType,
  Maybe,
  PrimaryIncome,
  RepaymentTerm,
} from '@graphql/index';
import { FormValues, ModifiedApplicationQuote } from '@graphql/types';
import { formStepSchemaMap } from '@pages/ApplicationForm/constants';
import {
  APPLICATION_STEPS,
  PRODUCTS_WITH_PREFILLING_DISABLED,
  getAdminUserId,
  getFormFields,
  isJointApplicant,
  pascalToCamelCase,
} from '@utils';

type ApplicantType = Applicant & {
  capacityToPay?: string | null;
  directDebitStatement?: string | null;
  creditAndTermsAgreement?: string | null;
};

type QueryEnterprise = NonNullable<NonNullable<ApplicationDetailsQuery['application']>['enterprise']>;

type ApplicationType = Omit<
  Partial<Application>,
  'id' | 'status' | 'documents' | 'productConfiguration' | 'enterprise'
> & {
  primaryApplicant?: ApplicantType;
  jointApplicant?: ApplicantType | null;
  enterprise?: QueryEnterprise | null;
};

type Payment = {
  methodId?: Maybe<string>;
  directDebit?: {
    type: RepaymentType;
    accountName: string;
    bsb: string;
    accountNumber: string;
  };
  creditCard?: {
    type: RepaymentType;
    cardHolder: string;
    maskedCardNumber: string;
    cardExpiry: string;
    token: string;
  };
  isCashApplication?: boolean | null;
};

type UpdatedApplicationType = Omit<ApplicationType, 'payment'> & {
  applicant?: Partial<ApplicantType>;
  financialProductId: string;
  payment: Payment;
};

const getDocumentDetails = (updatedApplication: UpdatedApplicationType, documentIds?: Maybe<string[]>) => {
  const allUploadedDocuments: ApplicationDocument[] = get(updatedApplication, 'documents');
  const filteredDocuments = allUploadedDocuments.filter(document => documentIds?.includes(document.id));
  return filteredDocuments.map(document => ({
    documentId: document.id,
    fileName: document.filename,
  }));
};

const getIncomeDetails = (
  updatedApplication: UpdatedApplicationType,
  income?: Maybe<PrimaryIncome> | Maybe<AdditionalIncome>,
) => {
  const documents = getDocumentDetails(updatedApplication, income?.documentIds);

  return {
    ...income,
    documents,
    bankStatementToken: income?.bankStatementToken ?? '',
    bankStatementComplete: !!income?.bankStatementToken,
    documentIds: income?.documentIds ?? [],
  };
};

const getFieldValue = (
  field: FieldPath<ApplicationFormValues>,
  fieldValue: FieldValues | string | boolean,
  stepData: Partial<ApplicationFormValues>,
  updatedApplication: UpdatedApplicationType,
) => {
  const isJoint = isJointApplicant();
  switch (field) {
    case 'agreeToFinanceTerms':
    case 'agreeToPrivacyTerms': {
      const isTermsAgreed = updatedApplication.applicant?.termsAndConditions !== null;
      set(stepData, field, isTermsAgreed);
      break;
    }
    case 'applicantType':
    case 'applicant.type': {
      const applicantType = isJoint ? ApplicantTypeDetail.Joint : ApplicantTypeDetail.Primary;
      set(stepData, field, applicantType);
      break;
    }
    case 'agreeToCustomerDeclaration': {
      const isTermsAgreed = updatedApplication.applicant?.termsAndConditions?.consentCustomerDeclaration !== null;
      set(stepData, field, isTermsAgreed);
      break;
    }
    case 'sresClaimed':
    case 'nswSafeguardClaimed':
    case 'hesdClaimed':
    case 'nathersAssessment': {
      const value = updatedApplication.applicant?.[field];
      if (value != null) {
        set(stepData, field, value);
      }
      break;
    }
    case 'payment.isCashApplication':
      set(stepData, field, !!fieldValue);
      break;
    case 'applicant.employment.type':
      set(stepData, field, EmploymentType[fieldValue as string]);
      break;
    case 'enterprise.structure':
      // Convert from GraphQL enum key (e.g. "SoleTrader") to validation enum value (e.g. "Sole Trader")
      set(stepData, field, EnterpriseStructure[fieldValue as string] ?? fieldValue);
      break;
    case 'enterprise.trustDeedDocumentIds': {
      // Persist document IDs and resolve filenames from the application documents array
      // so that trust deed file names are available after page reload.
      if (fieldValue && Array.isArray(fieldValue)) {
        set(stepData, field, fieldValue);
        const allDocuments: ApplicationDocument[] = get(updatedApplication, 'documents') ?? [];
        const documentNames = (fieldValue as string[]).map((documentId: string) => {
          const document = allDocuments.find(doc => doc.id === documentId);
          return document?.filename ?? documentId;
        });
        set(stepData, 'enterprise.trustDeedDocumentNames', documentNames);
      }
      break;
    }
    case 'applicant.income.isAdditionalIncome': {
      if (fieldValue) {
        const income = getIncomeDetails(updatedApplication, updatedApplication.applicant?.income?.additional);
        set(stepData, 'applicant.income.additional', income);
      }
      set(stepData, field, !!fieldValue);
      break;
    }
    case 'applicant.income.isSkipPrimaryIncome':
    case 'applicant.income.isSkipAdditionalIncome': {
      const isAdmin = !!getAdminUserId();
      // Skip income verification for only admin user else force to upload income verification documents
      set(stepData, field, isAdmin ? fieldValue : false);
      break;
    }
    case 'documents':
    case 'documents.documentId': {
      const documents = getDocumentDetails(
        updatedApplication,
        updatedApplication.applicant?.identification?.documentIds,
      );
      set(stepData, 'documents', documents);
      break;
    }
    case 'hasJointApplicant': {
      if (fieldValue && updatedApplication.jointApplicant) {
        const { firstName, lastName, email, phone } = updatedApplication.jointApplicant;
        const jointApplicant = {
          firstName,
          lastName,
          email,
          phone,
        };
        set(stepData, 'jointApplicant', jointApplicant);
      }
      set(stepData, field, fieldValue);
      break;
    }
    case 'isSameAddress': {
      const isSameAddress = isEqual(updatedApplication.address, updatedApplication.applicant?.address);
      set(stepData, field, isSameAddress);
      break;
    }
    case 'repaymentTerm':
      set(stepData, field, `${(fieldValue as RepaymentTerm).termInMonths}`);
      break;
    case 'termsAndConditions.directDebitStatement':
    case 'termsAndConditions.authoriseBrighteStatement':
    case 'termsAndConditions.capacityToPay':
      set(stepData, field, !!fieldValue);
      break;
    case 'applicant.isIncomeReviewConfirmation': {
      // If it is CDR application,
      // set isIncomeReviewConfirmation to undefined
      // The user needs to confirm the computed income again before submitting the application
      set(stepData, field, updatedApplication.applicant?.usingOpenBanking ? undefined : false);
      break;
    }
    case 'incomeVerification': {
      // incomeVerification is stored under applicant.income in the GraphQL schema
      // but lives at the top level of the IncomeVerification form values
      const value = updatedApplication.applicant?.income?.incomeVerification;
      if (value != null) {
        set(stepData, field, value);
      }
      break;
    }
    default: {
      const structure = updatedApplication?.enterprise?.structure;
      const structureProp = structure ? pascalToCamelCase(structure) : undefined;
      if (structureProp && field.includes(`enterprise.${structureProp}`)) {
        set(stepData, `enterprise.${structureProp}`, updatedApplication?.enterprise?.[structureProp]);
      }

      if (fieldValue != null) {
        if (
          (updatedApplication?.applicant?.identification?.type !== IdentificationType.MEDICARE &&
            field.includes('expiryDate')) ||
          field.includes('dateOfBirth')
        ) {
          set(stepData, field, new Date(fieldValue as string));
        } else if (field.includes('applicant.income.primary')) {
          const income = getIncomeDetails(updatedApplication, updatedApplication.applicant?.income?.primary);
          set(stepData, 'applicant.income.primary', income);
        } else {
          set(stepData, field, fieldValue);
        }
      }
      break;
    }
  }
};

export const transformFieldValues = (
  formSteps: string[],
  quote: ModifiedApplicationQuote,
  application?: ApplicationType,
  payment?: Payment | Record<string, never>,
): { formValues: Partial<Partial<FormValues>>; currentStepIndex: number } => {
  const financialProductId = quote.financialProduct.id;

  const isJoint = isJointApplicant();

  const updatedApplication = {
    ...application,
    applicant: isJoint
      ? {
          ...application?.jointApplicant,
        }
      : application?.primaryApplicant,
    financialProductId,
    payment: {
      ...application?.payment,
      ...payment,
    },
  };

  let currentStepIndex = 1;
  const applicationFormData = { quote };

  //Disabling save and continue feature for products other than brighte-sfa and electrify-2515
  if (PRODUCTS_WITH_PREFILLING_DISABLED.includes(financialProductId)) {
    return { formValues: applicationFormData, currentStepIndex };
  }

  const updatedSteps = formSteps.filter(step => step !== APPLICATION_STEPS.CONTACT_DETAILS);

  for (const [index, step] of updatedSteps.entries()) {
    const schema = formStepSchemaMap[step].schema;

    const formFields = getFormFields(schema, financialProductId);
    const stepData = {};

    formFields.forEach(field => {
      const fieldValue = get(updatedApplication, field);
      getFieldValue(field, fieldValue, stepData, updatedApplication);
    });

    // Save BusinessDetails and Eligibility before validation. For BusinessDetails, the enterprise
    // structure must be available to Eligibility at submission time. For Eligibility, product-specific
    // required fields (e.g. BHESL NSW program questions) may be null in the DB for applications
    // submitted before those fields were introduced — saving before validation ensures checkboxes
    // and any answered fields are still prefilled when the user returns to the step.
    if (step === APPLICATION_STEPS.BUSINESS_DETAILS || step === APPLICATION_STEPS.ELIGIBILITY) {
      applicationFormData[step] = stepData;
    }

    // Save step data to applicationFormData BEFORE validation. This is safe for all steps
    // because: (1) stepData originates from the DB, not user input; (2) on validation failure
    // the loop breaks immediately so no subsequent step reads this data; (3) the user sees
    // their persisted data instead of a blank form when returned to an incomplete step.
    // if (step === APPLICATION_STEPS.HOUSEHOLD_CASH) {
    //   applicationFormData[APPLICATION_STEPS.HOUSEHOLD] = stepData;
    // } else {
    //   applicationFormData[step] = stepData;
    // }

    try {
      if (step === APPLICATION_STEPS.SUMMARY) {
        currentStepIndex = index + 1;
        break;
      } else {
        schema.parse(stepData);
      }
    } catch {
      currentStepIndex = index + 1;
      break;
    }

    if (step === APPLICATION_STEPS.HOUSEHOLD_CASH) {
      applicationFormData[APPLICATION_STEPS.HOUSEHOLD] = stepData;
    } else {
      applicationFormData[step] = stepData;
    }
  }

  return { formValues: applicationFormData, currentStepIndex };
};
